import axios from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { ApiClientConfig, ApiLogger, TokenProvider } from '@hetivo/hetivo-lib-types';
import { generateCorrelationId } from './api-error';

interface ApiClientOptions {
  config: ApiClientConfig;
  /** Prefix for correlation IDs (e.g., "bo", "fo", "drv") */
  correlationIdPrefix?: string;
  logger?: ApiLogger;
  /** Optional callback invoked every time a request is sent. Used to notify auth provider of backend activity. */
  onRequestSent?: () => void;
  /** Callback invoked on 401 responses. Used to trigger sign-out when the user's session is invalidated server-side. */
  onUnauthorized?: () => void;
  tokenProvider?: TokenProvider;
}

interface RequestMetadata {
  startTime: number;
}

type ConfigWithMetadata = InternalAxiosRequestConfig & { _metadata?: RequestMetadata };

/** Methods Fastify parses a body for — these need an empty body rather than a stripped content-type. */
const BODY_METHODS = new Set(['patch', 'post', 'put']);

function methodExpectsBody(method: string | undefined): boolean {
  return BODY_METHODS.has((method ?? '').toLowerCase());
}

export class ApiClient {
  readonly instance: AxiosInstance;
  private readonly correlationIdPrefix: string;
  private readonly logger?: ApiLogger;
  private readonly onRequestSent?: () => void;
  private readonly onUnauthorized?: () => void;
  private readonly tokenProvider?: TokenProvider;

  constructor(options: ApiClientOptions) {
    const { config, correlationIdPrefix = 'app', logger, onRequestSent, onUnauthorized, tokenProvider } = options;

    this.correlationIdPrefix = correlationIdPrefix;
    this.logger = logger;
    this.onRequestSent = onRequestSent;
    this.onUnauthorized = onUnauthorized;
    this.tokenProvider = tokenProvider;

    this.instance = axios.create({
      baseURL: config.baseURL,
      headers: {
        'Content-Type': 'application/json',
        ...config.defaultHeaders,
      },
      timeout: config.timeout ?? 10_000,
    });

    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /**
   * Execute a request and return the response data.
   * Errors propagate naturally — error handling is done at the hook/component level.
   */
  async execute<T>(call: () => Promise<AxiosResponse<T>>): Promise<T> {
    const response = await call();
    return response.data;
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  /**
   * Makes a bodyless request one Fastify will actually accept. The instance stamps
   * `Content-Type: application/json` on every request, but axios sends NO body at all for a bodyless
   * call (`delete(url)`, `post(url)`), and Fastify rejects the two halves of that differently — so
   * neither half alone is a fix:
   *
   *   POST + content-type + no body  -> 400 "Body cannot be empty when content-type is set…"
   *   POST + no content-type         -> 415 Unsupported Media Type
   *   POST + content-type + `{}`     -> 200
   *   DELETE + no content-type       -> 200
   *
   * Hence the split: a body-carrying method gets an empty JSON body (keeping the header that now
   * describes it), while the rest drop the header that describes nothing. Left unhandled this 400s
   * before routing, which silently broke every bodyless DELETE and POST in the app — settings deletes,
   * vehicle restore, tenant impersonate. Requests that already carry data are untouched.
   */
  private normalizeBodylessRequest(config: ConfigWithMetadata): void {
    if (config.data !== undefined && config.data !== null) {
      return;
    }
    if (methodExpectsBody(config.method)) {
      config.data = {};
    } else {
      config.headers.delete('Content-Type');
    }
  }

  private setupRequestInterceptor(): void {
    this.instance.interceptors.request.use(async (config: ConfigWithMetadata) => {
      config._metadata = { startTime: Date.now() };

      const correlationId = generateCorrelationId(this.correlationIdPrefix);
      config.headers.set('x-correlation-id', correlationId);

      this.normalizeBodylessRequest(config);

      if (this.tokenProvider) {
        const token = await this.tokenProvider();
        if (token !== null) {
          config.headers.set('Authorization', `Bearer ${token}`);
        }
      }

      if (this.logger?.onRequest) {
        const method = config.method ?? 'unknown';
        const url = config.url ?? '';
        this.logger.onRequest(method, url);
      }

      this.onRequestSent?.();

      return config;
    });
  }

  private setupResponseInterceptor(): void {
    this.instance.interceptors.response.use(
      (response) => {
        this.logResponse(response);
        return response;
      },
      (error: unknown) => {
        this.logError(error);
        this.handleUnauthorized(error);
        return Promise.reject(error);
      },
    );
  }

  private handleUnauthorized(error: unknown): void {
    if (!this.onUnauthorized) {
      return;
    }
    const axiosError = error as { response?: { status?: number } };
    if (axiosError.response?.status === 401) {
      this.onUnauthorized();
    }
  }

  private logResponse(response: { config: ConfigWithMetadata; status: number }): void {
    const config = response.config;
    const durationMs = Date.now() - (config._metadata?.startTime ?? Date.now());

    if (this.logger?.onResponse) {
      const method = config.method ?? 'unknown';
      const url = config.url ?? '';
      this.logger.onResponse(method, url, response.status, durationMs);
    }
  }

  private extractErrorInfo(error: unknown): {
    durationMs: number;
    method: string;
    status: number | undefined;
    url: string;
  } {
    const axiosError = error as { config?: ConfigWithMetadata; response?: { status?: number } };
    return {
      durationMs: Date.now() - (axiosError.config?._metadata?.startTime ?? Date.now()),
      method: axiosError.config?.method ?? 'unknown',
      status: axiosError.response?.status,
      url: axiosError.config?.url ?? '',
    };
  }

  private logError(error: unknown): void {
    if (!this.logger?.onError) {
      return;
    }
    const { durationMs, method, status, url } = this.extractErrorInfo(error);
    this.logger.onError(method, url, status, durationMs, error);
  }
}
